import { Box, CircularProgress, Chip, Stack } from "@mui/material";
import Select from "react-select";
import { Button, Modal, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useState, useRef, useMemo, useEffect } from "react";

import {
  fetchColumnFilterOptions,
  setShowFilterModal,
  batchUpdateAppliedFilters,
  resetAppliedFilters,
  initialMissingMvdsFilters,
} from "../redux/ReportsSlice";

import { multiSelectStyles } from "../multiSelectStyle";
import { getColumnFilterEndpoints } from "../optionsConfig";
import closeIcon from "../../../../common/assets/images/x-close.png";
import { normalizeToArray } from "../hooks/useReport";

const REPORT_PATH = "/missing-mvds-report";

const FILTER_LABELS = {
  discharge_date_from: "Discharge From",
  discharge_date_to: "Discharge To",
  document_type: "Document Type",
  status: "MVDS Status",
  missing_notes_flag: "Missing Notes Flag",
  episode_id: "Episode ID",
  medical_record_number: "MRN",
  specialty: "Specialty",
  health_fund: "Health Fund",
};

const HIDDEN_FROM_CHIPS = [
  "page_number",
  "limit",
  "sort_by",
  "sort_order",
  "search",
];

/* ── Discharge Date Range presets ── */
const toYMD = (d) => d.toISOString().split("T")[0];

const DATE_RANGE_PRESETS = [
  {
    label: "Today",
    getRange: () => {
      const t = new Date();
      return { from: toYMD(t), to: toYMD(t) };
    },
  },
  {
    label: "Last 7 Days",
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { from: toYMD(from), to: toYMD(to) };
    },
  },
  {
    label: "Last 30 Days",
    getRange: () => {
      const today = new Date();

      const from = new Date(today);
      from.setDate(today.getDate() - 29);

      const to = new Date(today);
      to.setDate(today.getDate() - 7);

      return {
        from: toYMD(from),
        to: toYMD(to),
      };
    },
  },
  {
    label: "Last 30+ Days",
    getRange: () => {
      const from = new Date("2000-01-01");
      const to = new Date();
      to.setDate(to.getDate() - 30);
      return { from: "", to: toYMD(to) };
    },
  },
];

const MISSING_NOTES_FLAG_OPTIONS = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

const SelectLoadingIndicator = () => (
  <div style={{ display: "flex", alignItems: "center", padding: "0 8px" }}>
    <CircularProgress size={16} thickness={5} />
  </div>
);


const SelectLoadingMessage = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 12 }}>
    <CircularProgress size={16} thickness={5} />
    <span style={{ fontSize: 13, color: "#667085" }}>Loading...</span>
  </div>
);

const CoblMissingMvdsReportFilter = ({ onApply }) => {
  const dispatch = useDispatch();
  const debounceRef = useRef({});
  const pendingSearchRef = useRef({});

  const [searchLoading, setSearchLoading] = useState({});
  const [searchTexts, setSearchTexts] = useState({});
  const [local, setLocal] = useState({ ...initialMissingMvdsFilters });
  const [dateRangeLabel, setDateRangeLabel] = useState("");
  const [selectedLabelsCache, setSelectedLabelsCache] = useState({});

  const showFilterModal = useSelector((s) => s.reports.showFilterModal);

  const appliedFilters = useSelector(
    (s) => s.reports.appliedFilters[REPORT_PATH] || {},
  );

  const columnOptions = useSelector(
    (s) => s.reports.columnFilterOptions[REPORT_PATH] || {},
  );

  const columnConfig = getColumnFilterEndpoints(REPORT_PATH);

  /* ───────── Field groups (matches screenshot sections) ───────── */

  const frequentlyUsedFields = [
    { label: "Document Type", name: "document_type", type: "list" },
    { label: "MVDS Status", name: "status", type: "list" },
    { label: "Missing Notes Flag", name: "missing_notes_flag", type: "single-static", options: MISSING_NOTES_FLAG_OPTIONS },
  ];

  const episodeFields = [
    { label: "Episode ID", name: "episode_id", type: "list" },
    { label: "MRN Only", name: "medical_record_number", type: "list" },
  ];

  const clinicalFields = [
    { label: "Specialty", name: "specialty", type: "list" },
    { label: "Health Fund", name: "health_fund", type: "list" },
  ];

  /* ───────── Chips ───────── */

  const hasAny = useMemo(
    () =>
      Object.entries(local).some(([k, v]) => {
        if (HIDDEN_FROM_CHIPS.includes(k)) return false;
        return Array.isArray(v) ? v.length > 0 : Boolean(v);
      }),
    [local],
  );

  const chips = useMemo(() => {
    const list = [];

    if (dateRangeLabel) {
      list.push({ key: "discharge_date_range", value: dateRangeLabel, actualValue: dateRangeLabel });
    }

    Object.entries(local).forEach(([k, v]) => {
      if (HIDDEN_FROM_CHIPS.includes(k) || !v) return;
      if (k === "discharge_date_from" || k === "discharge_date_to") return; // shown via dateRangeLabel chip

      if (k === "status" && Array.isArray(v)) {
        const statusData = columnOptions?.status?.data || []; // 1. currently loaded page
        const cache = selectedLabelsCache?.status || {}; // 2. cached at time of selection
        v.forEach((id) => {
          const matched = statusData.find((item) => item.status_id === id || item.id === id);
          const label = matched?.status || cache[id] || id; // 3. raw id, last resort
          list.push({ key: k, value: label, actualValue: id });
        });
        return;
      }

      if (k === "missing_notes_flag") {
        const matched = MISSING_NOTES_FLAG_OPTIONS.find((o) => o.value === v);
        list.push({ key: k, value: matched?.label || v, actualValue: v });
        return;
      }

      if (Array.isArray(v)) {
        v.forEach((val) => list.push({ key: k, value: val, actualValue: val }));
        return;
      }

      list.push({ key: k, value: v, actualValue: v });
    });

    return list;
  }, [local, columnOptions, dateRangeLabel, selectedLabelsCache]);

  const removeChip = ({ key, actualValue }) => {
    if (key === "discharge_date_range") {
      setLocal((prev) => ({ ...prev, discharge_date_from: null, discharge_date_to: null }));
      setDateRangeLabel("");
      return;
    }

    setLocal((prev) => {
      const current = prev[key];
      if (Array.isArray(current)) {
        return { ...prev, [key]: current.filter((v) => v !== actualValue) };
      }
      return { ...prev, [key]: key === "missing_notes_flag" ? "" : null };
    });
  };

  /* ───────── Handlers ───────── */

  const handleChange = (name, value) => {
    setLocal((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangePreset = (preset) => {
    if (!preset) {
      setLocal((prev) => ({ ...prev, discharge_date_from: null, discharge_date_to: null }));
      setDateRangeLabel("");
      return;
    }
    const { from, to } = preset.getRange();
    setLocal((prev) => ({ ...prev, discharge_date_from: from, discharge_date_to: to }));
    setDateRangeLabel(preset.label);
  };

  const handleApplyFilters = () => {
    dispatch(
      batchUpdateAppliedFilters({
        reportPath: REPORT_PATH,
        filters: { ...local, page_number: 1 },
      }),
    );
    onApply?.(local);
    dispatch(setShowFilterModal(false));
  };

  const handleClearAll = () => {
    setLocal({ ...initialMissingMvdsFilters });
    setDateRangeLabel("");
  };

  const handleResetFilters = () => {
    dispatch(resetAppliedFilters({ reportPath: REPORT_PATH }));
    setLocal({ ...initialMissingMvdsFilters });
    setDateRangeLabel("");
    onApply?.(initialMissingMvdsFilters);
    dispatch(setShowFilterModal(false));
  };

  const mapToSelectOptions = (arr, fieldName) =>
    Array.isArray(arr)
      ? arr.map((i) => {
          if (fieldName === "status" && typeof i === "object") {
            return { label: i.status, value: i.status_id ?? i.id };
          }

          // SPECIALTY → API returns { hospital_speciality_id, speciality }
          // Payload wants the NAME, not the id, so label and value are both i.speciality
          if (fieldName === "specialty" && typeof i === "object") {
            return { label: i.speciality, value: i.speciality };
          }

          if (typeof i === "object") {
            return {
              label: i.label || i.name || i.status || i.value,
              value: i.id ?? i.status_id ?? i.value,
            };
          }
          return { label: i, value: i };
        })
      : [];

  const isFieldLoading = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    return Boolean(searchLoading[fieldName] || columnState?.loading);
  };

  const getOptionsWithLoader = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    const columnData = columnState?.data || [];
    return mapToSelectOptions(columnData, fieldName);
  };

  const dispatchFetch = (fieldName, search = "", page = 1) => {
    const config = columnConfig[fieldName];
    if (!config) return;

    dispatch(
      fetchColumnFilterOptions({
        reportPath: REPORT_PATH,
        columnKey: fieldName,
        endpoint: config.endpoint,
        responseKey: config.responseKey,
        search,
        page,
        limit: 10,
      }),
    );
  };
  const resetFieldToDefault = (fieldName) => {
    if (debounceRef.current[fieldName]) {
      clearTimeout(debounceRef.current[fieldName]);
    }
    setSearchTexts((prev) => ({ ...prev, [fieldName]: "" }));
    setSearchLoading((prev) => ({ ...prev, [fieldName]: true }));
    pendingSearchRef.current[fieldName] = true;
    dispatchFetch(fieldName, "", 1);
  };

  const handleInputChange = (fieldName, inputValue, { action } = {}) => {
  
    if (action !== "input-change") return;
    if (inputValue === "") {
      resetFieldToDefault(fieldName);
      return;
    }

    setSearchTexts((prev) => ({ ...prev, [fieldName]: inputValue }));

    setSearchLoading((prev) => ({ ...prev, [fieldName]: true }));
    pendingSearchRef.current[fieldName] = true;

    if (debounceRef.current[fieldName]) {
      clearTimeout(debounceRef.current[fieldName]);
    }

    debounceRef.current[fieldName] = setTimeout(() => {
      dispatchFetch(fieldName, inputValue, 1);
    }, 500);
  };

  const handleMenuOpen = (fieldName) => {
    const columnState = columnOptions?.[fieldName];

    if (!columnState || !columnState.isLoaded) {
      dispatchFetch(fieldName, "", 1);
      return;
    }

    const hasMore = columnState?.hasMore !== false;
    const count = columnState?.data?.length || 0;

    if (count < 5 && hasMore) {
      dispatchFetch(fieldName, searchTexts[fieldName] || "", (columnState.page || 1) + 1);
    }
  };

  const handleScrollBottom = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    if (!columnState || columnState.loading || columnState.hasMore === false) return;
    dispatchFetch(fieldName, searchTexts[fieldName] || "", (columnState.page ?? 1) + 1);
  };

  // Sync local state when modal opens
  useEffect(() => {
    if (showFilterModal) {
      setLocal({ ...initialMissingMvdsFilters, ...appliedFilters });

      const matched = DATE_RANGE_PRESETS.find((p) => {
        const r = p.getRange();
        return r.from === appliedFilters.discharge_date_from && r.to === appliedFilters.discharge_date_to;
      });
      setDateRangeLabel(matched?.label || (appliedFilters.discharge_date_from ? "Custom Range" : ""));
    }
  }, [showFilterModal]); 

  useEffect(() => {
    Object.keys(pendingSearchRef.current).forEach((fieldName) => {
      if (!pendingSearchRef.current[fieldName]) return;
      const columnState = columnOptions?.[fieldName];
      if (columnState && !columnState.loading) {
        pendingSearchRef.current[fieldName] = false;
        setSearchLoading((prev) => ({ ...prev, [fieldName]: false }));
      }
    });
  }, [columnOptions]);


  // after clearing the search box.
  useEffect(() => {
    Object.keys(local).forEach((fieldName) => {
      const columnState = columnOptions?.[fieldName];
      if (!columnState) return;

      if (pendingSearchRef.current[fieldName]) return;
      if (searchTexts[fieldName]) return;

      const columnData = normalizeToArray(columnState.data);
      const selectedValues = local[fieldName] || [];
      const remaining = columnData.length - selectedValues.length;

      if (remaining < 5 && !columnState.loading && columnState.hasMore !== false) {
        dispatchFetch(fieldName, "", (columnState.page ?? 1) + 1);
      }
    });
  }, [local, columnOptions]); // eslint-disable-line

  const selectStyles = {
    ...multiSelectStyles,
    placeholder: (provided) => ({
      ...provided,
      fontSize: "14px",
    }),
  };

  const getSelectedOptions = (field) => {
    const ids = local[field.name];
    if (!Array.isArray(ids)) return [];

    if (field.name === "status") {
      const statusData = columnOptions?.status?.data || [];
      const cache = selectedLabelsCache?.status || {};
      return ids.map((id) => {
        const matched = statusData.find((item) => item.status_id === id || item.id === id);
        return {
          value: id,
          label: matched?.status || cache[id] || String(id),
        };
      });
    }

    return mapToSelectOptions(ids, field.name);
  };

  const renderListField = (field) => (
    <Select
      isMulti
      className="filter-model-input-option-field"
      styles={selectStyles}
      options={getOptionsWithLoader(field.name)}
      value={getSelectedOptions(field)}
      closeMenuOnSelect={false}
      placeholder={`Search ${field.label}...`}
      isLoading={isFieldLoading(field.name)}
      components={{ LoadingIndicator: SelectLoadingIndicator }}
      loadingMessage={SelectLoadingMessage}
      filterOption={() => true}
      onMenuOpen={() => handleMenuOpen(field.name)}
      onMenuScrollToBottom={() => handleScrollBottom(field.name)}
      onInputChange={(inputValue, actionMeta) => handleInputChange(field.name, inputValue, actionMeta)}
      onChange={(selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
        handleChange(field.name, values);

        if (field.name === "status") {
          setSelectedLabelsCache((prev) => ({
            ...prev,
            status: {
              ...prev.status,
              ...Object.fromEntries((selectedOptions || []).map((opt) => [opt.value, opt.label])),
            },
          }));
        }
        if (searchTexts[field.name]) {
          resetFieldToDefault(field.name);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    />
  );

  const renderStaticSingleField = (field) => (
    <Select
      className="filter-model-input-option-field"
      styles={selectStyles}
      options={field.options}
      value={field.options.find((o) => o.value === local[field.name]) || null}
      placeholder={`Select ${field.label}`}
      isClearable
      onChange={(selected) => handleChange(field.name, selected ? selected.value : "")}
    />
  );

  return (
    <Modal
      className="error-drg-report-filters-modal"
      style={{ zIndex: "1500" }}
      show={showFilterModal}
      onHide={() => dispatch(setShowFilterModal(false))}
      size="lg"
      centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Missing MVDS Report Filters
          <div style={{ fontSize: 13, fontWeight: 400, color: "#667085", marginTop: 4 }}>
            Filter uncoded episodes waiting for Missing Minimum Viable Document Set (MVDS) documentation.
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Box>
          {/* ── FREQUENTLY USED ── */}
          <Row className="g-3 mb-2">
            <Col xs={12}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#667085", letterSpacing: 0.5 }}>
                <span style={{ borderLeft: "3px solid #1976d2", paddingLeft: 6 }}>FREQUENTLY USED</span>
              </div>
            </Col>

            <Col xs={12} sm={6} md={3} className="workflow-outstanding-report-filter-label">
              <label>Discharge Date Range</label>
              <Select
                className="filter-model-input-option-field"
                styles={selectStyles}
                options={DATE_RANGE_PRESETS.map((p) => ({ label: p.label, value: p.label }))}
                value={dateRangeLabel ? { label: dateRangeLabel, value: dateRangeLabel } : null}
                placeholder="Select range"
                isClearable
                onChange={(selected) => {
                  const preset = DATE_RANGE_PRESETS.find((p) => p.label === selected?.value);
                  handleDateRangePreset(preset || null);
                }}
              />
            </Col>

            <Col xs={12} sm={6} md={3} className="workflow-outstanding-report-filter-label">
              <label>{FILTER_LABELS.document_type}</label>
              {renderListField(frequentlyUsedFields[0])}
            </Col>

            <Col xs={12} sm={6} md={3} className="workflow-outstanding-report-filter-label">
              <label>{FILTER_LABELS.status}</label>
              {renderListField(frequentlyUsedFields[1])}
            </Col>

            <Col xs={12} sm={6} md={3} className="workflow-outstanding-report-filter-label">
              <label>{FILTER_LABELS.missing_notes_flag}</label>
              {renderStaticSingleField(frequentlyUsedFields[2])}
            </Col>
          </Row>

          <hr />

          {/* ── EPISODE FILTERS ── */}
          <Row className="g-3 mb-2">
            <Col xs={12}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#667085", letterSpacing: 0.5 }}>
                <span style={{ borderLeft: "3px solid #1976d2", paddingLeft: 6 }}>EPISODE FILTERS</span>
              </div>
            </Col>

            {episodeFields.map((field) => (
              <Col xs={12} sm={6} key={field.name} className="workflow-outstanding-report-filter-label">
                <label>{field.label}</label>
                {renderListField(field)}
              </Col>
            ))}
          </Row>

          <hr />

          {/* ── CLINICAL FILTERS ── */}
          <Row className="g-3 mb-2">
            <Col xs={12}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#667085", letterSpacing: 0.5 }}>
                <span style={{ borderLeft: "3px solid #1976d2", paddingLeft: 6 }}>CLINICAL FILTERS</span>
              </div>
            </Col>

            {clinicalFields.map((field) => (
              <Col xs={12} sm={6} key={field.name} className="workflow-outstanding-report-filter-label">
                <label>{field.label}</label>
                {renderListField(field)}
              </Col>
            ))}
          </Row>

          {hasAny && (
            <>
              <hr />
              <Row className="mb-2">
                <Col className="d-flex flex-row justify-content-between align-items-start">
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#667085", marginBottom: 4 }}>
                      ACTIVE FILTERS:
                    </div>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      sx={{ maxHeight: "80px", overflowY: "auto", overflowX: "hidden", alignContent: "flex-start" }}>
                      {chips.map((c, i) => (
                        <Chip
                          key={i}
                          className="m-1 bg-light"
                          size="small"
                          label={c.key === "discharge_date_range" ? c.value : `${FILTER_LABELS[c.key] || c.key}: ${c.value}`}
                          style={{ border: "1px solid #555", fontSize: "12px" }}
                          deleteIcon={<img src={closeIcon} alt="remove" className="episodeListFilterObj-close-icon-him" />}
                          onDelete={() => removeChip(c)}
                        />
                      ))}
                    </Stack>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Box>
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <Button variant="link" className="p-0" onClick={handleClearAll} style={{ textDecoration: "none" }}>
          Clear All
        </Button>
        <div>
          <Button
            className="el-filter-cancel btn btn-secondary me-2"
            onClick={() => dispatch(setShowFilterModal(false))}>
            Cancel
          </Button>
          <Button className="el-filter-submit" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CoblMissingMvdsReportFilter;